import React, { useCallback, useContext, useEffect, useState } from "react";

import { normalizeFluentVars, useNotificationContext } from "cerulean";
import { Localized, useLocalization } from "@fluent/react";
import {
  Button,
  Checkbox,
  IconButton,
  Row,
  Snackbar,
  Table,
} from "@narmi/design_system";
import cc from "classcat";
import ApiHttp from "byzantine/src/ApiHttp";
import { useCurrentUserFullName } from "byzantine";

import { useSidebarContainer } from "../../sidebar/sidebar.hooks";
import { CheckboxContext } from "../components/CheckboxContext";

import styles from "./DocumentsTable.module.scss";
import DocumentRow from "./DocumentRow";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

interface DocumentsTableProps {
  documents: WebDocument[];
  isHtmlJson: boolean;
  openDocumentShareDialog: () => void;
  // Fluent ID of the message shown when there are no documents.
  emptyMessageId?: string;
}

const DocumentsTable = ({
  documents,
  isHtmlJson,
  openDocumentShareDialog,
  emptyMessageId = "empty-filter-documents",
}: DocumentsTableProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { isCollapsed } = useSidebarContainer();
  const currentUserName = useCurrentUserFullName();
  const isFilteredToEmpty = documents.length === 0;
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const { checkBoxState, setCheckBoxState } = useContext(CheckboxContext);
  const boxesChecked = Object.values(checkBoxState).length;

  const downloadDocument = useCallback((url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // When `documents` changes, enforce that `boxesChecked` are only Documents
  // within `documents`.
  useEffect(() => {
    setCheckBoxState((states) => {
      const currentDocIds = new Set(documents.map((d) => d.id));
      return Object.fromEntries(
        Object.entries(states).filter(([docId]) =>
          currentDocIds.has(docId as API.DocumentId)
        )
      );
    });
  }, [documents, setCheckBoxState]);

  const toggleCheckbox = useCallback(
    (doc: WebDocument) => {
      setCheckBoxState((state) => {
        if (state[doc.id]) {
          const { [doc.id]: _, ...remainingState } = state;
          return remainingState;
        }
        return { ...state, [doc.id]: doc };
      });
    },
    [setCheckBoxState]
  );

  const handleDownload = useCallback(async () => {
    setIsLoadingDocument(true);
    const payload = {
      documents: Object.entries(checkBoxState).map(([doc_id, doc]) => ({
        ...doc,
        account_name: doc.account_id,
      })),
    };

    try {
      // Download all selected documents as a zip
      const response = await ApiHttp.fetch(
        "documents/download",
        { method: "POST", shouldGetBlob: true },
        payload
      );

      // Create blob from response and trigger download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const documentsTranslation = normalizeFluentVars(
        l10n.getString(
          "document-bulk-download-file-name",
          { currentUserName },
          `${currentUserName}-documents`
        )
      );
      downloadDocument(url, `${documentsTranslation}.zip`);
      URL.revokeObjectURL(url);
      setIsLoadingDocument(false);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          "document-bulk-download-failure",
          null,
          "Failed to download documents. Please try again later."
        ),
      });
      setIsLoadingDocument(false);
    }
  }, [
    checkBoxState,
    downloadDocument,
    currentUserName,
    l10n,
    sendNotification,
  ]);

  const getHeaderCheckboxTestId = () => {
    if (boxesChecked === 0) {
      return "header-checkbox-unchecked";
    }
    if (boxesChecked === documents.length) {
      return "header-checkbox-checked";
    }
    return "header-checkbox-indeterminate";
  };

  return (
    <div className={styles["documents-page-table"]}>
      <Table
        rowDensity="compact"
        colLayout={{
          s: isFilteredToEmpty
            ? "1fr"
            : "repeat(3, 1fr) min-content min-content",
          m: isFilteredToEmpty
            ? "1fr"
            : "2.66fr 2fr 1fr min-content min-content",
          l: isFilteredToEmpty
            ? "1fr"
            : "1.5fr 1fr 0.66fr min-content min-content",
        }}
        colVisibility={isFilteredToEmpty ? ["*"] : ["*", "*", "*", "l", "m"]}
      >
        <Table.Header>
          <div className={styles["documents-page-table-header-cell"]}>
            <Table.HeaderCell>
              <Row gapSize="xs">
                <Checkbox
                  kind="table"
                  testId={getHeaderCheckboxTestId()}
                  label={l10n.getString(
                    "document-list-select-all",
                    null,
                    "Select all documents"
                  )}
                  checked={
                    boxesChecked === documents.length && boxesChecked > 0
                  }
                  onChange={(_event: React.ChangeEvent<HTMLInputElement>) => {
                    if (boxesChecked > 0) {
                      setCheckBoxState({});
                    } else {
                      setCheckBoxState(
                        documents.reduce(
                          (acc, doc) => ({ ...acc, [doc.id]: doc }),
                          {}
                        )
                      );
                    }
                  }}
                  indeterminate={
                    boxesChecked > 0 && boxesChecked < documents.length
                  }
                />
                <span data-testid="documents-header-type">
                  <Localized id="document-list-type-header">
                    Document type
                  </Localized>
                </span>
              </Row>
            </Table.HeaderCell>
          </div>
          {!isFilteredToEmpty ? (
            <>
              <div className={styles["documents-page-table-header-cell"]}>
                <Table.HeaderCell>
                  <span data-testid="documents-header-account">
                    <Localized id="document-list-account-header">
                      Account
                    </Localized>
                  </span>
                </Table.HeaderCell>
              </div>
              <div className={styles["documents-page-table-header-cell"]}>
                <Table.HeaderCell>
                  <span data-testid="documents-header-date">
                    <Localized id="document-list-date-header">Date</Localized>
                  </span>
                </Table.HeaderCell>
              </div>
              <div className={styles["documents-page-table-header-cell"]}>
                <Table.HeaderCell> </Table.HeaderCell>
              </div>
              <div className={styles["documents-page-table-header-cell"]}>
                <Table.HeaderCell> </Table.HeaderCell>
              </div>
            </>
          ) : null}
        </Table.Header>

        {isFilteredToEmpty ? (
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <span data-testid="documents-empty-message">
                  <Localized id={emptyMessageId} />
                </span>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        ) : (
          <Table.Body>
            {documents.map((doc) => {
              return (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  toggleCheckbox={toggleCheckbox}
                  isHtmlJson={isHtmlJson}
                />
              );
            })}
          </Table.Body>
        )}
      </Table>
      {Object.values(checkBoxState).some(Boolean) && (
        <div
          data-testid="documents-table-snackbar"
          className={cc([
            styles["documents-page-table-snackbar"],
            isCollapsed &&
              styles["documents-page-table-snackbar--sidebar-collapsed"],
          ])}
        >
          <Snackbar isActive>
            <Snackbar.Text>
              <div className={styles["documents-page-table-snackbar-text"]}>
                <span>
                  <b>
                    {l10n.getString(
                      "document-list-snackbar-selected",
                      { num: boxesChecked },
                      `${boxesChecked} selected`
                    )}
                  </b>
                  &nbsp;&nbsp;
                  {l10n.getString(
                    "document-list-snackbar-zip-file",
                    null,
                    "ZIP file"
                  )}
                </span>
                <IconButton
                  name="x"
                  kind="action"
                  textSize="l"
                  onClick={() => setCheckBoxState({})}
                />
              </div>
            </Snackbar.Text>
            <Snackbar.Divider />
            <Snackbar.ButtonGroup>
              <Button
                kind="secondary"
                label={l10n.getString(
                  "document-list-snackbar-share",
                  null,
                  "Share"
                )}
                onClick={openDocumentShareDialog}
              />
              <Button
                kind="primary"
                isLoading={isLoadingDocument}
                label={l10n.getString(
                  "document-list-snackbar-download",
                  { num: boxesChecked },
                  `Download (${boxesChecked})`
                )}
                testId="snackbar-download"
                onClick={handleDownload}
              />
            </Snackbar.ButtonGroup>
          </Snackbar>
        </div>
      )}
    </div>
  );
};

export default DocumentsTable;
